import { Node, mergeAttributes, wrappingInputRule } from "@tiptap/core";

export type CalloutType = "info" | "warning" | "tip" | "danger";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    callout: {
      setCallout: (options: { type: CalloutType }) => ReturnType;
      toggleCallout: (options: { type: CalloutType }) => ReturnType;
    };
  }
}

/**
 * Matches `:::info`, `:::warning`, `:::tip`, `:::danger` at the start of a line.
 * The captured group determines the callout type.
 */
const CALLOUT_INPUT_REGEX = /^:::(info|warning|tip|danger)\s$/;

export const Callout = Node.create({
  name: "callout",

  group: "block",

  content: "block+",

  defining: true,

  addAttributes() {
    return {
      type: {
        default: "info" as CalloutType,
        parseHTML: (element) =>
          (element.getAttribute("data-type") as CalloutType) ?? "info",
        renderHTML: (attributes) => ({
          "data-type": attributes.type as string,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-callout="true"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const type = (HTMLAttributes["data-type"] as string) ?? "info";

    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-callout": "true",
        class: `callout callout-${type}`,
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setCallout:
        ({ type }) =>
        ({ commands }) => {
          return commands.wrapIn(this.name, { type });
        },
      toggleCallout:
        ({ type }) =>
        ({ commands }) => {
          return commands.toggleWrap(this.name, { type });
        },
    };
  },

  addInputRules() {
    return [
      wrappingInputRule({
        find: CALLOUT_INPUT_REGEX,
        type: this.type,
        getAttributes: (match) => ({
          type: match[1] as CalloutType,
        }),
      }),
    ];
  },
});
